import React from 'react';
import UpdateLayout, { ExternalLink } from './UpdateLayout';
import {
  DELIVERY_CONTEXT,
  GOVERNANCE_BY_DEFAULT_AS_OF,
  GOVERNANCE_BY_DEFAULT_SOURCES,
} from '../../../content/snowflakeUpdates';

const TAGS_NOTE =
  'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-20-snowflake-provided-tags-ga';
const REPORTS_NOTE =
  'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-20-sensitive-data-entitlement-access-reports-ga';

const h2Class = 'pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900';

export default function GovernanceByDefaultPost() {
  return (
    <UpdateLayout
      path="/updates/snowflake-governance-by-default"
      title="Snowflake-provided tags and Trust Center sensitive data reports are GA"
      eyebrow="Governance by default"
      chip="Governance"
      asOf={GOVERNANCE_BY_DEFAULT_AS_OF}
      sources={GOVERNANCE_BY_DEFAULT_SOURCES}
    >
      <h2 id="what-shipped" className={h2Class}>
        What shipped
      </h2>
      <p>
        On October 20, 2026, Snowflake-provided tags became generally available (
        <ExternalLink href={TAGS_NOTE}>Snowflake-provided tags release note</ExternalLink>
        ). These out-of-the-box tags in the <code>SNOWFLAKE.TAGS</code> schema give you a
        consistent vocabulary for common governance use cases, including cost center,
        certification status, sensitivity, environment, and project, without creating and
        maintaining equivalent tags in each account.
      </p>
      <p>
        The same day, the Trust Center Sensitive Data Entitlement and Access reports became
        generally available (
        <ExternalLink href={REPORTS_NOTE}>
          Sensitive Data Entitlement and Access reports release note
        </ExternalLink>
        ). These reports require Enterprise Edition or higher. The Sensitive Data Entitlement
        report lists the users, roles, and privileges that grant access to tables containing
        sensitive data. The Sensitive Data Access report lists users who accessed those tables
        during a configurable lookback period.
      </p>

      <h2 id="why-standard-tags-matter" className={h2Class}>
        Why standard tags matter
      </h2>
      <p>
        Cost center, sensitivity, and environment labels only help when they mean the same
        thing in every account. A shared cost center tag is what keeps cost allocation
        consistent across accounts, and the same shared names keep sensitivity and environment
        labeling aligned.
      </p>

      <h2 id="using-the-reports-in-security-reviews" className={h2Class}>
        Using the reports in security reviews
      </h2>
      <p>
        In a security review, one report shows who can reach the tables. The other shows who
        reached them in the period you set.
      </p>

      <h2 id="caveats" className={h2Class}>
        Caveats
      </h2>
      <p>
        The Trust Center reports and <code>SKIP_TAG_PROPAGATION</code> require Enterprise Edition
        or higher.
      </p>

      <div className="rounded-xl border border-brand-200 bg-brand-50 p-6">
        <h2 id="rollout-checklist" className={h2Class}>
          Rollout checklist
        </h2>
        <ol className="mt-4 list-decimal space-y-3 pl-5">
          <li>
            <span className="font-semibold text-ink-900">Decide which provided tags to adopt.</span>{' '}
            Start from the vocabulary in <code>SNOWFLAKE.TAGS</code>: cost center, certification
            status, sensitivity, environment, and project. Treat <code>SKIP_TAG_PROPAGATION</code>{' '}
            as its own decision.
          </li>
          <li>
            <span className="font-semibold text-ink-900">Map them to existing custom tags.</span>{' '}
            Where an account already has a custom tag for the same job, write down which provided
            tag replaces it, and which custom tags you will keep.
          </li>
          <li>
            <span className="font-semibold text-ink-900">Manage tag assignments as code.</span>{' '}
            Keep the assignments in Terraform, and promote them through CI/CD with Azure DevOps
            Pipelines or GitHub Actions.
          </li>
          <li>
            <span className="font-semibold text-ink-900">Apply them on dbt models where that fits.</span>{' '}
            Set the tags through dbt <code>config</code> or <code>meta</code> on the models that
            should carry them.
          </li>
          <li>
            <span className="font-semibold text-ink-900">Review the Trust Center reports on a schedule.</span>{' '}
            Read the Sensitive Data Entitlement report and the Sensitive Data Access report on a
            cadence you choose.
          </li>
        </ol>
      </div>

      <h2 id="how-myorglab-helps" className={h2Class}>
        How MyOrgLab helps
      </h2>
      <p>
        MyOrgLab builds governed analytics in the customer&apos;s own account. {DELIVERY_CONTEXT}
      </p>
    </UpdateLayout>
  );
}
